import { InfoCircleOutlined } from '@ant-design/icons';
import caregiversNoAi from 'assets/caregivers-no-ai.png';
import laurine from 'assets/laurine.jpg';
import MessageInput from 'components/inputs/MessageInput';
import PaywallModal from 'components/modals/PaywallModal';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import useIsKeyboardVisible from 'hooks/useIsKeyboardVisible';
import usePreferences from 'hooks/usePreferences';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectDoesUserHaveFullAccess } from 'store/users/selectors';
import ScrollUtil from 'utils/ScrollUtil';

export type ChatExplainerProps = {
  scrollableElement: React.RefObject<HTMLDivElement>;
  disableNewConversationButton?: boolean;
};

const SPECIALTY_TAGS_MEDICAL = [
  { emoji: '🌸', labelKey: 'chats.explainerV2.specialties.gynecologists' },
  { emoji: '🌿', labelKey: 'chats.explainerV2.specialties.midwives' },
  { emoji: '🤲', labelKey: 'chats.explainerV2.specialties.physiotherapists' },
  { emoji: '🥑', labelKey: 'chats.explainerV2.specialties.nutritionists' },
  { emoji: '💬', labelKey: 'chats.explainerV2.specialties.psychologists' },
  { emoji: '👶', labelKey: 'chats.explainerV2.specialties.childcareNurses' },
  { emoji: '🦴', labelKey: 'chats.explainerV2.specialties.osteopaths' },
  { emoji: '👩‍⚕️', labelKey: 'chats.explainerV2.specialties.nurses' },
];

const SPECIALTY_TAGS_SOFT = [
  { emoji: '🌱', labelKey: 'chats.explainerV2.specialties.naturopaths' },
  { emoji: '🧘', labelKey: 'chats.explainerV2.specialties.sophrologists' },
];

const overlineClassName = 'm-0 text-2xs font-bold tracking-widest uppercase';
const stepClassName = 'flex flex-row items-start gap-4';
const stepNumberClassName =
  'flex size-9 shrink-0 items-center justify-center rounded-full bg-info-muted text-sm font-bold text-foreground';
const stepContentClassName = 'flex flex-col gap-1 pt-1';
const stepTitleClassName = 'm-0 text-base font-bold text-foreground';
const stepDescriptionClassName = 'm-0 text-sm leading-normal text-muted-foreground';
const stepConnectorClassName = '-my-2 ml-4 h-6 w-0.5 bg-info-muted';
export default function ChatExplainer({ scrollableElement, disableNewConversationButton = false }: ChatExplainerProps) {
  const { t } = useTranslation();
  const { isKeyboardVisible } = useIsKeyboardVisible();
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const doesUserHaveFullAccess = useSelector(selectDoesUserHaveFullAccess);

  const { value: didEnableFreeMessagesValue, set: setDidEnableFreeMessages } = usePreferences(
    Keys.PAYWALL_DID_ENABLE_FREE_MESSAGES,
  );
  const didEnableFreeMessages = didEnableFreeMessagesValue === true;

  const [isStarted, setIsStarted] = useState(false);
  const [isInspirationModalOpen, setIsInspirationModalOpen] = useState(false);

  const exampleMessages = t<string, { returnObjects: true }, string[]>('chats.new.exampleMessages', {
    returnObjects: true,
  });

  const handleStart = () => {
    if (doesUserHaveFullAccess || didEnableFreeMessages) {
      setIsStarted(true);
      posthog.capture('chats.start_conversation_clicked', { source: 'explainer_v2' });
    } else {
      setIsPaywallOpen(true);
      posthog.capture('paywall.opened', { source: 'explainer_v2' });
    }
  };

  const ctaRef = useRef<HTMLDivElement>(null);
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  useEffect(() => {
    if (disableNewConversationButton) return;
    const el = ctaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setShowFloatingCta(!entry.isIntersecting), {
      root: scrollableElement.current,
      threshold: 1,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [disableNewConversationButton, scrollableElement]);

  useEffect(() => {
    if (isStarted) {
      scrollableElement.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isStarted, scrollableElement]);

  useEffect(() => {
    if (isKeyboardVisible) {
      ScrollUtil.scrollToBottom(scrollableElement.current);
    }
  }, [isKeyboardVisible, scrollableElement]);

  return (
    <div className="flex w-full flex-col bg-background pb-16">
      {/* Hero section */}
      <div className="flex flex-col gap-3 px-4 pt-6 pb-4">
        {/* leaves room for the close button */}
        <h3 className="pr-8">
          <Trans
            i18nKey="chats.explainerV2.heroTitle"
            components={{ italic: <span className="text-foreground italic" /> }}
          />
        </h3>
        <p className="m-0 text-base leading-normal text-muted-foreground">{t('chats.explainerV2.heroSubtitle')}</p>

        <div className="mt-8 mb-2 flex flex-col gap-3 rounded-lg bg-card p-4 shadow-sm">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center gap-2.5">
              <div
                className="relative flex size-11 items-center justify-center rounded-full bg-info-muted text-xl"
                ref={ctaRef}
              >
                <img src={laurine} alt="" className="size-full rounded-full object-cover" />
                <div className="absolute right-px bottom-px size-2.5 rounded-full border-2 border-card bg-success" />
              </div>
              <div>
                <p className="m-0 text-base font-bold text-foreground">{t('chats.explainerV2.caregiverName')}</p>
                <p className="m-0 text-xs text-muted-foreground">{t('chats.explainerV2.caregiverRole')}</p>
              </div>
            </div>
          </div>

          {isStarted ? (
            <div className="w-full">
              <MessageInput animatePlaceholder minRows={2} containerStyle={{ paddingRight: 0 }} compact />
              <Button variant="link" className="w-full" onClick={() => setIsInspirationModalOpen(true)}>
                {t('chats.explainer.noInspiration')}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-col">
                <p className="m-0 max-w-4/5 self-end rounded-lg rounded-br-sm bg-chat-me px-3 py-2 text-sm text-chat-me-foreground">
                  {t('chats.explainerV2.patientBubble')}
                </p>
                <p className="max-w-4/5 self-start rounded-lg rounded-bl-sm bg-chat-them px-3 py-2 mt-2 text-sm text-chat-them-foreground">
                  {t('chats.explainerV2.caregiverBubble')}
                </p>
              </div>

              {!disableNewConversationButton && (
                <Button size="xl" className="w-full" onClick={handleStart}>
                  {t('chats.explainerV2.startConversationCta')}
                </Button>
              )}
            </>
          )}

          <p className="m-0 text-center text-xs text-subtle-foreground">{t('chats.explainerV2.trustBadges')}</p>
        </div>
      </div>

      {/* Team section */}
      <div className="mx-4 my-8 flex flex-col gap-3 rounded-lg bg-info-muted p-5">
        <p className={`${overlineClassName} flex items-center gap-1.5 text-foreground`}>
          <img src={caregiversNoAi} alt="" className="size-6 rounded-md" />
          {t('chats.explainerV2.teamLabel')}
        </p>
        <h4>{t('chats.explainerV2.teamTitle')}</h4>
        <p className="m-0 mb-1 text-sm leading-normal text-muted-foreground">{t('chats.explainerV2.teamSubtitle')}</p>
        <p className="my-1 text-2xs font-bold tracking-wider text-foreground uppercase">
          {t('chats.explainerV2.specialties.groupMedical')}
        </p>
        <div className="flex flex-wrap gap-2">
          {SPECIALTY_TAGS_MEDICAL.map(tag => (
            <Badge key={tag.labelKey} variant="outline">
              {tag.emoji} {t(tag.labelKey)}
            </Badge>
          ))}
        </div>
        <p className="my-1 text-2xs font-bold tracking-wider text-foreground uppercase">
          {t('chats.explainerV2.specialties.groupSoft')}
        </p>
        <div className="flex flex-wrap gap-2">
          {SPECIALTY_TAGS_SOFT.map(tag => (
            <Badge key={tag.labelKey} variant="outline">
              {tag.emoji} {t(tag.labelKey)}
            </Badge>
          ))}
        </div>
      </div>

      {/* How it works section */}
      <div className="flex flex-col gap-2 px-4 pt-8 pb-4">
        <p className={`${overlineClassName} text-muted-foreground`}>{t('chats.explainerV2.howItWorksLabel')}</p>
        <h3>
          <Trans
            i18nKey="chats.explainerV2.howItWorksTitle"
            components={{ italic: <span className="text-foreground italic" /> }}
          />
        </h3>

        <div className="mt-2 flex flex-col gap-6">
          {/* Step 1 */}
          <div className={stepClassName}>
            <div className={stepNumberClassName}>01</div>
            <div className={stepContentClassName}>
              <p className={stepTitleClassName}>{t('chats.explainerV2.step1Title')}</p>
              <p className={stepDescriptionClassName}>{t('chats.explainerV2.step1Description')}</p>
            </div>
          </div>
          <div className={stepConnectorClassName} />

          {/* Step 2 */}
          <div className={stepClassName}>
            <div className={stepNumberClassName}>02</div>
            <div className={stepContentClassName}>
              <p className={stepTitleClassName}>{t('chats.explainerV2.step2Title')}</p>
              <p className={stepDescriptionClassName}>{t('chats.explainerV2.step2Description')}</p>
            </div>
          </div>
          <div className={stepConnectorClassName} />

          {/* Step 3 */}
          <div className={stepClassName}>
            <div className={stepNumberClassName}>03</div>
            <div className={stepContentClassName}>
              <p className={stepTitleClassName}>{t('chats.explainerV2.step3Title')}</p>
              <p className={stepDescriptionClassName}>{t('chats.explainerV2.step3Description')}</p>
            </div>
          </div>
          <div className={stepConnectorClassName} />

          {/* Step 4 */}
          <div className={stepClassName}>
            <div className={stepNumberClassName}>04</div>
            <div className={stepContentClassName}>
              <p className={stepTitleClassName}>{t('chats.explainerV2.step4Title')}</p>
              <p className={stepDescriptionClassName}>{t('chats.explainerV2.step4Description')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quote section */}
      <div className="mx-4 mt-8 mb-4 flex flex-col gap-2 rounded-lg bg-info-muted p-5">
        <p className="m-0 text-lg leading-snug text-foreground italic">
          <Trans i18nKey="chats.explainerV2.quote" components={{ italic: <span className="font-bold italic" /> }} />
        </p>
        <p className="m-0 text-sm leading-normal text-muted-foreground">{t('chats.explainerV2.quoteDescription')}</p>
      </div>

      {/* Emergency */}
      <p className="m-0 px-4 pt-2 pb-6 text-center text-xs text-muted-foreground [&_svg]:mr-1.5">
        <InfoCircleOutlined />
        {t('chats.explainer.inCaseOfEmergency')}
      </p>

      <Dialog open={isInspirationModalOpen} onOpenChange={open => !open && setIsInspirationModalOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle asChild>
              <h3>Que dire dans mon premier message ?</h3>
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm [&_p]:mb-2 [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5">
            <Trans
              i18nKey="chats.explainer.firstMessageModal"
              components={{ strong: <strong />, p: <p />, ul: <ul />, li: <li />, quote: <q /> }}
            />
            <ul>{Array.isArray(exampleMessages) && exampleMessages.map(msg => <li key={msg}>{msg}</li>)}</ul>
          </div>
        </DialogContent>
      </Dialog>

      <PaywallModal
        isOpen={isPaywallOpen}
        onClose={() => setIsPaywallOpen(false)}
        closeModal={() => setIsPaywallOpen(false)}
        onFreeMessagesEnabled={() => {
          setDidEnableFreeMessages('true');
          setIsStarted(true);
        }}
      />

      {showFloatingCta &&
        !disableNewConversationButton &&
        scrollableElement.current?.parentElement &&
        createPortal(
          // Portalled into pageContainer (position: relative) so it sits above
          // the scroll content but below the nav bar.
          <div className="absolute right-4 bottom-4 left-4 z-[100] drop-shadow-md animate-in fade-in slide-in-from-bottom-2 duration-200">
            <Button
              size="xl"
              className="w-full"
              onClick={() => {
                if (isStarted) {
                  scrollableElement.current?.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  handleStart();
                }
              }}
            >
              {t('chats.explainerV2.startConversationCta')}
            </Button>
          </div>,
          scrollableElement.current.parentElement,
        )}
    </div>
  );
}
